import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, AlertCircle, CreditCard, Calendar, ArrowRight } from "lucide-react";

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { withCredentials: true });
        
        if (res.data.status === "complete" && res.data.payment_status === "paid") {
          setStatus("success");
          setSubscriptionInfo(res.data);
        } else if (res.data.status === "expired") {
          setStatus("error");
          setError("A sessão de pagamento expirou");
        } else {
          // Still processing, check again in a few seconds
          setTimeout(checkStatus, 2000);
        }
      } catch (err) {
        console.error("Error checking subscription status:", err);
        setStatus("error");
        setError("Erro ao verificar o estado da subscrição");
      }
    };
    
    if (sessionId) {
      checkStatus();
    } else {
      setStatus("error");
      setError("Sessão de pagamento não encontrada");
    }
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl rounded-2xl overflow-hidden">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 mx-auto mb-6 text-[#6697a4] animate-spin" />
            <h1 className="text-2xl font-bold text-[#3d5a63] mb-2">A processar...</h1>
            <p className="text-[#64748b]">A confirmar o teu pagamento com o Stripe</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl rounded-2xl overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#3d5a63] mb-2">Algo correu mal</h1>
            <p className="text-[#64748b] mb-6">{error}</p>
            <Button onClick={() => navigate("/student")} className="bg-[#6697a4] hover:bg-[#3d5a63]">
              Voltar ao Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-0 shadow-xl rounded-2xl overflow-hidden">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-[#6697a4] to-[#3d5a63] p-8 text-center text-white">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Subscrição Ativada!</h1>
          <p className="text-white/80">O teu pagamento foi processado com sucesso</p>
        </div>

        <CardContent className="p-8">
          {/* Subscription Details */}
          <div className="bg-[#f8fafc] rounded-xl p-6 mb-6">
            <h2 className="text-sm font-semibold text-[#94A3B8] uppercase tracking-wider mb-4">Detalhes da Subscrição</h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[#64748b] flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Plano
                </span>
                <span className="font-semibold text-[#3d5a63]">{subscriptionInfo?.plan_name || "Mensalidade"}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-[#64748b]">Preço Mensal</span>
                <span className="font-semibold text-[#3d5a63]">{subscriptionInfo?.amount || 30}€/mês</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-[#64748b]">Créditos Semanais</span>
                <span className="font-semibold text-[#3d5a63]">{subscriptionInfo?.weekly_credits || 2} créditos</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-[#64748b] flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> Faturação
                </span>
                <span className="font-semibold text-green-600">Automática (Stripe)</span>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="mb-6">
            <h3 className="font-semibold text-[#3d5a63] mb-3">O que acontece agora?</h3>
            <ul className="space-y-2 text-sm text-[#64748b]">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Os teus {subscriptionInfo?.weekly_credits || 2} créditos semanais já foram adicionados</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Recebes novos créditos automaticamente todas as semanas</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>O pagamento é renovado automaticamente todos os meses</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Podes cancelar a qualquer momento no teu dashboard</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <Button 
            onClick={() => navigate("/student")} 
            className="w-full bg-[#6697a4] hover:bg-[#3d5a63] h-12 text-base"
            data-testid="go-to-dashboard-btn"
          >
            Ir para o Dashboard <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
